/**
 * The employee self-service route table (CLAUDE.md §8.12).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `ESS_ROUTES` in.
 *
 * Three things about these paths are deliberate:
 *
 *  - **They all live under `/me`**, matching the API exactly. `/me/payslips` is
 *    a different screen from `/payroll/runs/7`'s payslip list, not the same
 *    screen with a filter, and the URL says which one you are on. A link
 *    pasted into a chat is unambiguous about whose figures are behind it.
 *  - **The only dynamic segment anywhere is a payslip id**, constrained to
 *    `\d+`. There is no employee id in any of these URLs and there never can
 *    be: every `/api/v1/me/...` route resolves the employee from the JWT, so a
 *    screen that needed an id in its path would have reached for the wrong
 *    endpoint. `routes.test.ts` asserts this rather than trusting it.
 *  - **Every route names the permission the Employee role actually holds.** The
 *    server registry has no "any authenticated user" option, so each screen is
 *    gated on the code the server guards its primary read with:
 *    `hr.employee.self.read` for the profile, `payroll.payslip.read` for
 *    payslips, `hr.leave.read` for leave, `hr.attendance.read` for attendance
 *    and `hr.employee.self.documents` for the file. Requesting leave needs
 *    `hr.leave.request` on top, and the button — not the route — checks that:
 *    an employee who may see their balances but not raise a request should
 *    still reach the screen.
 */

import type { RouteRecordRaw } from 'vue-router'

import { ESS_PERMISSIONS } from '@/modules/ess/permissions'

export const ESS_ROUTES: RouteRecordRaw[] = [
  {
    path: '/me',
    name: 'ess-profile',
    component: () => import('@/modules/ess/MyProfileView.vue'),
    meta: { titleKey: 'ess.routeTitles.profile', permission: ESS_PERMISSIONS.selfRead },
  },
  {
    path: '/me/payslips',
    name: 'ess-payslips',
    component: () => import('@/modules/ess/MyPayslipsListView.vue'),
    meta: { titleKey: 'ess.routeTitles.payslips', permission: ESS_PERMISSIONS.payslipRead },
  },
  {
    /* The id is a payslip's, never an employee's. `\d+` so `/me/payslips/foo`
       404s in the router rather than reaching the API as a bad parameter. */
    path: '/me/payslips/:id(\\d+)',
    name: 'ess-payslip',
    component: () => import('@/modules/ess/MyPayslipView.vue'),
    meta: { titleKey: 'ess.routeTitles.payslip', permission: ESS_PERMISSIONS.payslipRead },
  },
  {
    path: '/me/leave',
    name: 'ess-leave',
    component: () => import('@/modules/ess/MyLeaveView.vue'),
    meta: { titleKey: 'ess.routeTitles.leave', permission: ESS_PERMISSIONS.leaveRead },
  },
  {
    path: '/me/attendance',
    name: 'ess-attendance',
    component: () => import('@/modules/ess/MyAttendanceView.vue'),
    meta: { titleKey: 'ess.routeTitles.attendance', permission: ESS_PERMISSIONS.attendanceRead },
  },
  {
    path: '/me/documents',
    name: 'ess-documents',
    component: () => import('@/modules/ess/MyDocumentsView.vue'),
    meta: { titleKey: 'ess.routeTitles.documents', permission: ESS_PERMISSIONS.selfDocuments },
  },
]

/* ------------------------------------------------------------ own screens */

export const ESS_PATHS = {
  profile: '/me',
  payslips: '/me/payslips',
  payslip: (payslipId: number) => `/me/payslips/${payslipId}`,
  leave: '/me/leave',
  attendance: '/me/attendance',
  documents: '/me/documents',
} as const

/* -------------------------------------------------------- links elsewhere */

/**
 * One's own timesheets, in `modules/projects`.
 *
 * The other half of self-service already exists there and made the same
 * "this is your week, not everybody's" distinction (§8.13). ESS links to it
 * rather than reimplementing it; `modules/projects` owns that URL.
 */
export const OWN_TIMESHEETS_ROUTE = '/projects/timesheets'
